import { CommonConstants } from "utils/constants";
import RegularExpressions from "./regularExpressions";

const PersonNumberHelper = {
  normalize: (personNumber: string, normalizeToLong: boolean) => {
    const normalized = PersonNumberHelper.clearOtherCharacters(personNumber);
    switch (normalized.length) {
      case 10: {
        if (!normalizeToLong) {
          return personNumber;
        } else {
          const yearIncludingCentury = PersonNumberHelper.getYearIncludingCentury(Number(normalized.substr(0, 2)));
          return `${yearIncludingCentury}${personNumber.substring(2)}`;
        }
      }
      case 12: {
        if (!normalizeToLong) {
          return personNumber.substring(2);
        } else {
          return personNumber;
        }
      }
      default:
        return personNumber;
    }
  },
  getYearIncludingCentury: (yearInTwoDigits: number) => {
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    let guessedYear = currentCentury + yearInTwoDigits;
    if (currentYear - guessedYear < CommonConstants.MinimumExpectedAge) {
      guessedYear -= 100;
    }
    return guessedYear;
  },
  clearOtherCharacters: (personalNumber: string) => personalNumber.replace(/-|\+|\s/g, "").trim(),
  /**
   * Validates if a personal number matches the expected format pattern.
   * @param personalNumber - The personal number to validate
   * @returns True if the format is valid, false otherwise
   */
  isValidFormat: (personalNumber: string): boolean => {
    if (!personalNumber) return false;
    return RegularExpressions.PersonNumber.test(personalNumber);
  },
  /**
   * Formats a personal number to a standardized 12-digit format.
   * Removes all hyphens and adds century prefix (19/20) for 10-digit numbers.
   * @param personalNumber - The personal number to format (may contain hyphens)
   * @returns The formatted 12-digit personal number, or null if invalid length
   */
  formatToTwelveDigits: (personalNumber: string): string | null => {
    const cleaned = personalNumber.replace(/-/g, "");

    if (cleaned.length === 12) return cleaned;

    if (cleaned.length === 10) {
      const year = parseInt(cleaned.substring(0, 2), 10);
      const currentYear = new Date().getFullYear() % 100;
      const prefix = year > currentYear ? "19" : "20";
      return `${prefix}${cleaned}`;
    }

    return null;
  },
  /**
   * Extracts and validates birth date from a personal number.
   * Automatically cleans the input (removes hyphens, spaces, etc.).
   * Supports both 10-digit (YYMMDD-XXXX) and 12-digit (YYYYMMDD-XXXX) formats.
   * @param personalNumber - The personal number (may contain separators)
   * @returns The birth date as a Date object, or null if invalid
   */
  getBirthDateFromSSN: (personalNumber: string): Date | null => {
    const cleaned = PersonNumberHelper.clearOtherCharacters(personalNumber);

    let year: number;
    let month: number;
    let day: number;

    if (cleaned.length === 12) {
      // 12-digit format: YYYYMMDDXXXX
      year = parseInt(cleaned.substring(0, 4), 10);
      month = parseInt(cleaned.substring(4, 6), 10);
      day = parseInt(cleaned.substring(6, 8), 10);
    } else if (cleaned.length === 10) {
      // 10-digit format: YYMMDDXXXX
      const yearInTwoDigits = parseInt(cleaned.substring(0, 2), 10);
      year = PersonNumberHelper.getYearIncludingCentury(yearInTwoDigits);
      month = parseInt(cleaned.substring(2, 4), 10);
      day = parseInt(cleaned.substring(4, 6), 10);
    } else {
      return null;
    }

    // Samordningsnummer (coordination number) has 60 added to the day
    if (day > 60) {
      day -= 60;
    }

    const birthDate = new Date(year, month - 1, day);

    // Validate date is valid
    if (birthDate.getFullYear() !== year || birthDate.getMonth() !== month - 1 || birthDate.getDate() !== day) {
      return null;
    }

    return birthDate;
  },
  /**
   * Calculates age from a birth date.
   * @param birthDate - The birth date
   * @returns The age in years
   */
  calculateAge: (birthDate: Date): number => {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  },
  /**
   * Calculates age from a personal number.
   * Automatically cleans the input (removes hyphens, spaces, etc.).
   * Supports both 10-digit (YYMMDD-XXXX) and 12-digit (YYYYMMDD-XXXX) formats.
   * @param personalNumber - The personal number (may contain separators)
   * @returns The age in years, or null if invalid
   */
  calculateAgeFromSSN: (personalNumber: string): number | null => {
    const birthDate = PersonNumberHelper.getBirthDateFromSSN(personalNumber);
    if (!birthDate) return null;
    return PersonNumberHelper.calculateAge(birthDate);
  },
  /**
   * Checks if a personal number is a samordningsnummer (coordination number).
   * A samordningsnummer has the day part between 61-91 (regular days 01-31 + 60).
   * @param personalNumber - The personal number to check (may contain separators)
   * @returns True if it's a samordningsnummer, false otherwise
   */
  isCoordinationNumber: (personalNumber: string): boolean => {
    if (!personalNumber) return false;

    const cleaned = PersonNumberHelper.clearOtherCharacters(personalNumber);
    let day: number;

    if (cleaned.length === 12) {
      // 12-digit format: YYYYMMDDXXXX
      day = parseInt(cleaned.substring(6, 8), 10);
    } else if (cleaned.length === 10) {
      // 10-digit format: YYMMDDXXXX
      day = parseInt(cleaned.substring(4, 6), 10);
    } else {
      return false;
    }

    // Samordningsnummer has day between 61-91
    return day >= 61 && day <= 91;
  },
};

export default PersonNumberHelper;
